import { EventType } from "@/analytics/analytics-events";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { PointCloudObject } from "@/object-cache";
import { selectActiveAnalysisId } from "@/store/point-cloud-analysis-tool-selector";
import {
  PointCloudAnalysis,
  analysisExtentPoints,
  setActiveAnalysisId,
  setAnalysisVisibility,
  setLastCreatedAnalysisId,
  setShowActiveAnalysis,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectVisibilityDistance } from "@/store/view-options/view-options-selectors";
import { MeasuresSorter } from "@/tools/multi-point-measures/measures-sorter";
import { Analytics } from "@faro-lotv/foreign-observers";
import { UPDATE_CAMERA_MONITOR_PRIORITY } from "@faro-lotv/spatial-ui";
import { useFrame, useThree } from "@react-three/fiber";
import { Fragment, useEffect, useMemo, useState, type JSX } from "react";
import { Plane, Vector3 } from "three";
import { AnnotationVisibility } from "../annotations/annotation-utils";
import { isInsideClippingPlanes } from "../annotations/annotations-renderer";
import { AnalysisRenderer } from "./analysis-renderer";
import { CollapsedAnalysisRenderer } from "./collapsed-analysis-renderer";

type AnalysesRendererProps = {
  /** The current active point cloud */
  pointCloud: PointCloudObject;

  /** The list of analyses to render */
  analyses: PointCloudAnalysis[];

  /** Optional clipping planes */
  clippingPlanes?: Plane[];
};

/** @returns The renderer of the analyses stored in the app store */
export function AnalysesRenderer({
  analyses,
  pointCloud,
  clippingPlanes,
}: AnalysesRendererProps): JSX.Element {
  const labelContainer = useViewOverlayRef();
  const dispatch = useAppDispatch();

  const camera = useThree((s) => s.camera);

  const sorter = useMemo(() => new MeasuresSorter(camera), [camera]);

  const [analysisMarkerVisibility, setAnalysisMarkerVisibility] = useState(
    new Array<AnnotationVisibility>(),
  );

  const visibleAnalyses = useMemo(() => {
    if (clippingPlanes?.length !== 6) {
      return analyses;
    }
    const ret = new Array<PointCloudAnalysis>();
    for (const analysis of analyses) {
      if (analysis.kind === "cad") {
        ret.push(analysis);
        continue;
      }
      const position = new Vector3(0, 0, 0);
      const points = analysisExtentPoints(analysis);
      for (const [x, y, z] of points) {
        position.add(new Vector3(x, y, z));
      }
      position.divideScalar(points.length);
      if (isInsideClippingPlanes(position, clippingPlanes)) {
        ret.push(analysis);
      }
    }
    return ret;
  }, [analyses, clippingPlanes]);

  const visibilityDistance = useAppSelector(selectVisibilityDistance);
  const activeAnalysisId = useAppSelector(selectActiveAnalysisId);

  useEffect(() => {
    const foundActiveAnalys = visibleAnalyses.some(
      (analysis) => analysis.id === activeAnalysisId,
    );
    dispatch(setShowActiveAnalysis(foundActiveAnalys));
  }, [activeAnalysisId, dispatch, visibleAnalyses]);

  useFrame(({ camera }, delta) => {
    sorter.update(
      camera,
      visibleAnalyses,
      undefined,
      delta,
      visibilityDistance,
      visibilityDistance,
    );
    if (sorter.dirty()) {
      // Updating React states inside useFrame leads to downgraded performances.
      // Therefore, the 'analysisMarkerVisibility' state is updated only once every
      // 'sorter.secsBeforeUpdate' seconds.
      setAnalysisMarkerVisibility(sorter.measuresVisibility.slice());
      sorter.resetDirty();
    }
  }, UPDATE_CAMERA_MONITOR_PRIORITY);

  return (
    <>
      {visibleAnalyses.map((analysis, index) => {
        if (analysis.parentId !== pointCloud.iElement.id) {
          return null;
        }

        const visible = analysis.id === activeAnalysisId && analysis.isVisible;
        return (
          <Fragment key={analysis.id}>
            {visible && (
              <AnalysisRenderer
                pointCloud={pointCloud}
                analysis={analysis}
                clippingPlanes={clippingPlanes}
              />
            )}

            <CollapsedAnalysisRenderer
              visible={
                !visible &&
                analysisMarkerVisibility[index] !==
                  AnnotationVisibility.NotVisible
              }
              polygonSelection={analysisExtentPoints(analysis)}
              parentRef={labelContainer}
              onClick={() => {
                Analytics.track(EventType.showColoredAnalysis);

                dispatch(
                  setAnalysisVisibility({
                    analysisId: analysis.id,
                    visible: true,
                  }),
                );

                dispatch(setLastCreatedAnalysisId(undefined));
                dispatch(setActiveAnalysisId(analysis.id));
              }}
            />
          </Fragment>
        );
      })}
    </>
  );
}
